import React, { useState, useEffect, useMemo, useRef } from 'react'
import './MainHub.css'
import { useAuth } from '../../Auth';
import '../../client-dashboard/clientDashboardMap.css';
import Chart from 'react-apexcharts';
import Select, { components, MultiValue } from "react-select";
import { useLocation } from "react-router-dom";

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap, LeafletEvent } from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';

// Assets
import fnnPopupLogo from '../../img/FNNLogoWithBlackFont-06.png'
import redMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-red.png'
import yellowMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-yellow.png'
import greenMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-grey.png'
import googleMapsLogo from '../../backend/assets/google-map-logo.png'

// Icons
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

// Define the Detector type for TypeScript
interface DetectorObject {
    mac: string;
    ip: string;
    last_active: string;
    online: boolean;
    status: {
        site_name: string;
        gps_fix: boolean;
        gps_lat: number;
        gps_long: number;
        up_since: string;
        memory: string;
        fw_version: string;
        test_mode: boolean;
        thr: string;
        linux: boolean;
        sim_card_no?: string;
    };
}

interface RegionCountry {
    GROUP_NAME: string;
    Belongs_to: string;
}

// Custom styles to hide the selected chips/pills
const customStyles = {
    multiValue: (provided: any) => ({
        ...provided,
        display: "none" // hide chips
    }),
    control: (provided: any) => ({
        ...provided,
        backgroundColor: "#1e1e1e",
        borderColor: "#444",
        color: "#fff",
        minHeight: "40px",
        boxShadow: "none"
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#1e1e1e",
        zIndex: 9999
    }),
    option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? "#28aee1" // highlight selected
            : state.isFocused
                ? "#333"
                : "transparent",
        color: "#fff",
        cursor: "pointer"
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: "#fff"
    }),
    input: (provided: any) => ({
        ...provided,
        color: "#fff"
    })
};

// Custom ValueContainer that always shows placeholder unless typing
const ValueContainer = ({ children, ...props }: any) => {
    const { inputValue, placeholder } = props.selectProps;
    const isTyping = inputValue && inputValue.length > 0;

    return (
        <components.ValueContainer {...props}>
            {children}
            {!isTyping && placeholder && (
                <div
                    style={{
                        position: "absolute",
                        left: 12,
                        color: "#888",
                        pointerEvents: "none"
                    }}
                >
                    {placeholder}
                </div>
            )}
        </components.ValueContainer>
    );
};

// Search cities and addresses logic
interface Result {
    lon: string;
    lat: string;
    display_name: string;
    boundingbox: string[];
}

const DjangoNominatimProvider = {
    search: (() => {
        let timeout: NodeJS.Timeout | null = null;
        let controller: AbortController | null = null;

        return ({ query }: { query: string }) => {
            if (!query) return Promise.resolve([]);

            if (controller) controller.abort();
            if (timeout) clearTimeout(timeout);

            return new Promise((resolve, reject) => {
                timeout = setTimeout(() => {
                    controller = new AbortController();

                    fetch(`/api/geocode?q=${encodeURIComponent(query)}`, {
                        signal: controller.signal,
                    })
                        .then((response) => response.json())
                        .then((results: Result[]) => {
                            resolve(
                                results.map((r) => ({
                                    x: parseFloat(r.lon),
                                    y: parseFloat(r.lat),
                                    label: r.display_name,
                                    bounds: [
                                        [parseFloat(r.boundingbox[0]), parseFloat(r.boundingbox[2])],
                                        [parseFloat(r.boundingbox[1]), parseFloat(r.boundingbox[3])],
                                    ],
                                    raw: r,
                                }))
                            );
                        })
                        .catch((e) => {
                            if ((e as any).name === "AbortError") return;
                            reject(e);
                        });
                }, 200);
            });
        };
    })(),
};

const SearchControl: React.FC = () => {
    const map = useMap();

    useEffect(() => {
        const searchControl = new (GeoSearchControl as any)({
            provider: DjangoNominatimProvider,
            style: "bar",
            showMarker: false,
            showPopup: true,
            popupFormat: ({ query, result }: { query: string; result: { label: string } }) =>
                `You searched for: ${query}<br>Result: ${result.label}`,
            searchLabel: "Search Address or Zip",
        });

        map.addControl(searchControl);

        return () => {
            map.removeControl(searchControl);
        };
    }, [map]);

    return null;
};

export const redIcon = new Icon({
    iconUrl: redMarker,
    iconSize: [50, 50]
});

export const yellowIcon = new Icon({
    iconUrl: yellowMarker,
    iconSize: [50, 50]
});

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50]
});

export const greenIcon = new Icon({
    iconUrl: greenMarker,
    iconSize: [50, 50]
});

// Creating chart for collor coding the markers and text based on memory and gps fix
const getColorForMemory = (count: number) => {
    if (count > 15) return "red"; //red
    if (count > 0.5 && count <= 15) return "#d5b60a"; //yellow
    if (count <= 0.5) return "#008000"; //green
};

const getColorForGPSFix = (isOn: boolean) => {
    if (isOn === false) return "red"; //red
    if (isOn === true) return "#008000"; //green
};

const getColorForMarker = (isOn: boolean, memoryValue: string | number) => {
    // Check if memoryValue is a string containing letters
    if (typeof memoryValue === "string" && /[a-zA-Z]/.test(memoryValue)) {
        return redIcon; // Ignore and return redIcon
    }

    // Convert memoryValue to a number
    const numericMemory = Number(memoryValue);

    if (isOn === false) return BwIcon;
    if (numericMemory > 15 && isOn === true) return redIcon; //red
    if (numericMemory > 0.5 && numericMemory <= 15 && isOn === true) return yellowIcon; //yellow
    if (numericMemory <= 0.5 && isOn === true) return greenIcon; //green

    return redIcon; // Default to redIcon in case of unexpected values
};

interface MainHubProps {
    onRegionChange?: (regions: string[]) => void;
}

const MainHub: React.FC<MainHubProps> = ({ onRegionChange }) => {

    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top when the component mounts
    }, []);

    const { isAuthenticated, getDashboardPermissions } = useAuth();

    const [regions, setRegions] = useState<string[]>([]);
    const location = useLocation();
    const preselectedRegion = location.state?.preselectedRegion || "All";
    const [selectedRegions, setSelectedRegions] = useState<string[]>([preselectedRegion]);

    const [statusFilter, setStatusFilter] = useState<"all" | "online" | "offline">("all");
    // Expanded/collapsed state for all regions
    const [expandedRegions, setExpandedRegions] = useState<{ [region: string]: boolean }>({});

    // helper to format region names
    const formatRegionName = (name: string) =>
        name.replace(/^detectors_/, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');


    // ✅ Convert regions into react-select options
    const regionOptions = regions.map(region => ({
        value: region,
        label: formatRegionName(region)
    }));

    const handleRegionChange = (
        selected: MultiValue<{ value: string; label: string }>
    ) => {
        if (!selected || selected.length === 0) {
            setSelectedRegions(["All"]);
            onRegionChange?.(["All"]); // 🔑 notify parent
            return;
        }

        const values = selected.map((opt) => opt.value.trim());
        const last = values[values.length - 1];

        if (last === "All" || last === "All + Test Detectors") {
            setSelectedRegions([last]);
            onRegionChange?.([last]); // 🔑 notify parent
            return;
        }

        const filtered = values.filter(
            (v) => v !== "All" && v !== "All + Test Detectors"
        );

        const final = filtered.length > 0 ? filtered : ["All"];
        setSelectedRegions(final);
        onRegionChange?.(final); // 🔑 notify parent
    };



    // regional detectors (per region)
    const [detectorDataRegionalized, setDetectorDataRegionalized] = useState<{ [region: string]: DetectorObject[] }>({});

    // all + test detectors (monitoring endpoint)
    const [detectorDataMonitoring, setDetectorDataMonitoring] = useState<DetectorObject[]>([]);

    const mapRef = useRef<LeafletMap | null>(null);

    // Fetch the countries each region belongs to
    const [regionCountries, setRegionCountries] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await fetch("/api/detectordashapi/", {
                    credentials: "same-origin"
                });
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const data: RegionCountry[] = await res.json();

                // Convert to { detectors_florida: "USA", detectors_sao_paulo: "Brazil", ... }
                const mapping: Record<string, string> = {};
                data.forEach(item => {
                    mapping[item.GROUP_NAME] = item.Belongs_to;
                });
                setRegionCountries(mapping);
            } catch (err) {
                console.error("Error fetching countries:", err);
                setRegionCountries({});
            }
        };

        fetchCountries();
    }, []);

    // Fetch all+test detectors
    useEffect(() => {
        const fetchMonitoring = async () => {
            try {
                const response = await fetch('/apidet/detector/monitoring', {
                    credentials: 'same-origin',
                });
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();

                let alljson: any = {};
                let alljson2: any = {};

                if (data.dataUSA && !data.dataUSA.error) {
                    const cleanedString = data.dataUSA.replace(/\\n/g, '').trim();
                    alljson = JSON.parse(cleanedString);
                }
                if (data.dataBR && !data.dataBR.error) {
                    const cleanedStringBR = data.dataBR.replace(/\\n/g, '').trim();
                    alljson2 = JSON.parse(cleanedStringBR);
                }

                // Flatten all regions into a single array
                // Flatten all regions into a single array and cast to DetectorObject[]
                const combined = [
                    ...Object.values(alljson).flat(),
                    ...Object.values(alljson2).flat()
                ].filter((d): d is DetectorObject => !!d && typeof d === "object" && "mac" in d);

                setDetectorDataMonitoring(combined);

            } catch (err) {
                console.error("Error fetching monitoring:", err);
                setDetectorDataMonitoring([]);
            }
        };

        fetchMonitoring();
        const interval = setInterval(fetchMonitoring, 500000);
        return () => clearInterval(interval);
    }, []);

    // Fetch regional detectors
    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const groups = await getDashboardPermissions();
                const detectorRegions = Object.keys(groups).filter(key => key.startsWith('detectors_'));

                setRegions(["All", "All + Test Detectors", ...detectorRegions]);

                // only set "All" if no preselectedRegion exists or is invalid
                setSelectedRegions(prev => {
                    if (prev.length > 0 && regions.includes(prev[0])) {
                        return prev; // keep preselected
                    }
                    if (preselectedRegion && ["All", "All + Test Detectors", ...detectorRegions].includes(preselectedRegion)) {
                        return [preselectedRegion];
                    }
                    return ["All"];
                });



                const detectorsData: { [region: string]: DetectorObject[] } = {};
                await Promise.all(
                    detectorRegions.map(async (regionKey) => {
                        try {
                            const res = await fetch(`/apidet/${regionKey}/`, { credentials: 'same-origin' });
                            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                            const data = await res.json();

                            if (Array.isArray(data)) {
                                detectorsData[regionKey] = data;
                            } else if (data?.Message) {
                                console.warn(`No detectors in region ${regionKey}:`, data.Message);
                                detectorsData[regionKey] = [];
                            } else {
                                detectorsData[regionKey] = [];
                            }
                        } catch (err) {
                            console.error(`Error fetching detectors for region ${regionKey}:`, err);
                            detectorsData[regionKey] = [];
                        }
                    })
                );

                setDetectorDataRegionalized(detectorsData);
            } catch (err) {
                console.error("Error fetching dashboard permissions:", err);
                setRegions([]);
                setDetectorDataRegionalized({});
            }
        };

        fetchRegions();
    }, [getDashboardPermissions]);

    // Determine selected detectors ignoring duplicates
    const selectedDetectors = useMemo(() => {
        if (selectedRegions.length === 0) return [];

        if (selectedRegions.includes("All")) {
            const combined = Object.values(detectorDataRegionalized).flat();
            return Array.from(new Map(combined.map(det => [det.mac, det])).values());
        }

        if (selectedRegions.includes("All + Test Detectors")) {
            return detectorDataMonitoring;
        }

        // Merge multiple regions and dedupe by MAC
        const combined = selectedRegions.flatMap(region => detectorDataRegionalized[region] || []);
        return Array.from(new Map(combined.map(det => [det.mac, det])).values());
    }, [selectedRegions, detectorDataRegionalized, detectorDataMonitoring]);



    const filteredDetectors = useMemo(() => {
        let detectors = selectedDetectors;

        if (statusFilter === "online") {
            detectors = detectors.filter(det => det.online);
        } else if (statusFilter === "offline") {
            detectors = detectors.filter(det => !det.online);
        }

        return detectors;
    }, [selectedDetectors, statusFilter]);

    // Function to open popup when clicking on a detector from the dashboard
    const markerRefs = useRef<{ [key: string]: any }>({});
    const openPopup = (mac: string) => {
        const marker = markerRefs.current[mac];
        if (marker) {
            marker.openPopup();
            const { lat, lng } = marker.getLatLng();
            mapRef.current?.flyTo([lat, lng], 7); // optional: zoom to detector
        }
    };

    // helper: haversine distance in km
    function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371; // Earth radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) *
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    // 🔑 Zoom when region changes
    useEffect(() => {
        if (!mapRef.current || selectedRegions.length === 0) return;

        // Case 1: World view for "All" or "All + Test Detectors"
        if (
            selectedRegions.includes("All") ||
            selectedRegions.includes("All + Test Detectors")
        ) {
            mapRef.current.flyTo([0.0, 0.0], 2);
            return;
        }

        // Case 2: Single region → first detector
        if (selectedRegions.length === 1) {
            const region = selectedRegions[0];
            const detectors = detectorDataRegionalized[region] || [];
            if (detectors.length > 0) {
                const first = detectors[0];
                const lat = Number(first.status.gps_lat);
                const lng = Number(first.status.gps_long);
                if (!isNaN(lat) && !isNaN(lng)) {
                    mapRef.current.flyTo([lat, lng], 7);
                }
            }
            return;
        }

        // Case 3: Multiple regions → average lat/long of first detector of each region
        let totalLat = 0;
        let totalLng = 0;
        let count = 0;
        const coords: [number, number][] = [];

        selectedRegions.forEach((region) => {
            const detectors = detectorDataRegionalized[region] || [];
            if (detectors.length > 0) {
                const first = detectors[0];
                const lat = Number(first.status.gps_lat);
                const lng = Number(first.status.gps_long);
                if (!isNaN(lat) && !isNaN(lng)) {
                    totalLat += lat;
                    totalLng += lng;
                    count++;
                    coords.push([lat, lng]);
                }
            }
        });

        if (count > 0) {
            const avgLat = totalLat / count;
            const avgLng = totalLng / count;

            // ✅ Check if regions span multiple countries
            const uniqueCountries = new Set(
                selectedRegions.map((r) => regionCountries[r]).filter(Boolean)
            );

            let zoomLevel = 4;
            if (uniqueCountries.size > 1) {
                zoomLevel = 2;
            } else {
                // same country → check distances
                let maxDistance = 0;
                for (let i = 0; i < coords.length; i++) {
                    for (let j = i + 1; j < coords.length; j++) {
                        const d = haversineDistance(
                            coords[i][0], coords[i][1],
                            coords[j][0], coords[j][1]
                        );
                        if (d > maxDistance) maxDistance = d;
                    }
                }

                // if close enough (<300 km), zoom tighter
                if (maxDistance < 300) {
                    zoomLevel = 6;
                }
            }

            mapRef.current.flyTo([avgLat, avgLng], zoomLevel);
        }
    }, [selectedRegions, detectorDataRegionalized, regionCountries]);


    // ----------------------------------------------- Chart functions -----------------------------------------------

    const [chartOptions, setChartOptions] = useState<any>({});
    type ApexSeries = number[] | { name: string; data: number[] }[];
    const [chartSeries, setChartSeries] = useState<ApexSeries>([]);
    const [isStackedBar, setIsStackedBar] = useState(false);

    // Universal abbreviation function
    const abbreviateRegion = (name: string) => {
        const formatted = formatRegionName(name);
        const words = formatted.split(' ');

        if (words.length === 1) {
            // Single-word region: use first 3 letters (uppercase)
            return words[0].slice(0, 3).toUpperCase();
        }

        // Multi-word region: abbreviate first word using US state or country code if applicable
        const firstWord = words[0].length <= 2 ? words[0].toUpperCase() : words[0].slice(0, 2).toUpperCase();

        // For the rest, keep first 3 letters to make it readable
        const rest = words.slice(1).map(w => w.slice(0, 3)).join('');

        return `${firstWord} ${rest}`;
    };

    useEffect(() => {
        if (selectedRegions.length === 0) return;

        if (selectedRegions.includes("All") || selectedRegions.includes("All + Test Detectors")) {
            const regionNames = Object.keys(detectorDataRegionalized);

            // Build stats with % online
            const regionData = regionNames.map(region => {
                const detectors = detectorDataRegionalized[region] || [];
                const onlineCount = detectors.filter(d => d.online).length;
                const offlineCount = detectors.length - onlineCount;
                const total = detectors.length;
                const percentOnline = total > 0 ? (onlineCount / total) * 100 : 0;

                return {
                    region,
                    percentOnline,
                    online: onlineCount,
                    offline: offlineCount
                };
            });

            // Sort by highest % first
            regionData.sort((a, b) => b.percentOnline - a.percentOnline);

            // Extract arrays for chart + lookup
            const sortedRegions = regionData.map(r => r.region);
            const percentOnline = regionData.map(r => r.percentOnline);
            const regionStats: { [key: string]: { online: number; offline: number } } = {};
            regionData.forEach(r => {
                regionStats[r.region] = { online: r.online, offline: r.offline };
            });

            setIsStackedBar(true);
            setChartSeries([{ name: '% Online', data: percentOnline }]);
            setChartOptions((prev: any) => ({
                ...prev,
                chart: { type: 'bar', stacked: false, toolbar: { show: true } },
                colors: ['#15FF00'],
                plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: '50%' } },
                dataLabels: {
                    enabled: true,
                    formatter: (val: number) => `${val.toFixed(1)}%`,
                    style: { colors: ['#ffffff'] }
                },
                xaxis: {
                    categories: sortedRegions.map(abbreviateRegion),
                    labels: { style: { colors: '#ffffff' } },
                    axisBorder: { show: false }, // removes x-axis line
                    axisTicks: { show: false }   // removes small ticks under labels
                },

                yaxis: {
                    title: { text: undefined },  // no label
                    labels: { show: false }      // no numbers
                },
                grid: {
                    yaxis: { lines: { show: false } } // no horizontal lines
                },
                legend: { position: 'bottom', labels: { colors: '#ffffff' } },
                tooltip: {
                    custom: function ({ series, seriesIndex, dataPointIndex }: any) {
                        const region = sortedRegions?.[dataPointIndex];
                        if (!region) {
                            return `<div style="background:#000;color:#fff;padding:6px;border-radius:4px;">No data</div>`;
                        }

                        const stats = regionStats[region] || { online: 0, offline: 0 };
                        const { online, offline } = stats;

                        return `
                <div style="background:#000;color:#fff;border-radius:4px;overflow:hidden;font-size:12px;min-width:140px;">
                    <div style="background:#111;padding:4px 8px;font-weight:bold;">
                        ${formatRegionName(region)}
                    </div>
                    <div style="background:#15FF00;color:#000;padding:4px 8px;">
                        Online: ${online} detector(s)
                    </div>
                    <div style="background:#a6a6a6;color:#fff;padding:4px 8px;">
                        Offline: ${offline} detector(s)
                    </div>
                </div>
            `;
                    }
                },
                fill: { opacity: 1 }
            }));

        }

        else if (selectedRegions.length > 1) {
            // Only selected subset → % online stacked bar
            const regionNames = selectedRegions;

            // Build stats with % online
            const regionData = regionNames.map(region => {
                const detectors = detectorDataRegionalized[region] || [];
                const onlineCount = detectors.filter(d => d.online).length;
                const offlineCount = detectors.length - onlineCount;
                const total = detectors.length;
                const percentOnline = total > 0 ? (onlineCount / total) * 100 : 0;

                return {
                    region,
                    percentOnline,
                    online: onlineCount,
                    offline: offlineCount
                };
            });

            // Sort by highest % first
            regionData.sort((a, b) => b.percentOnline - a.percentOnline);

            // Extract arrays for chart + lookup
            const sortedRegions = regionData.map(r => r.region);
            const percentOnline = regionData.map(r => r.percentOnline);
            const regionStats: { [key: string]: { online: number; offline: number } } = {};
            regionData.forEach(r => {
                regionStats[r.region] = { online: r.online, offline: r.offline };
            });

            setIsStackedBar(true);
            setChartSeries([{ name: '% Online', data: percentOnline }]);
            setChartOptions((prev: any) => ({
                ...prev,
                chart: { type: 'bar', stacked: false, toolbar: { show: true } },
                colors: ['#15FF00'],
                plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: '50%' } },
                dataLabels: {
                    enabled: true,
                    formatter: (val: number) => `${val.toFixed(1)}%`,
                    style: { colors: ['#ffffff'] }
                },
                xaxis: {
                    categories: sortedRegions.map(abbreviateRegion),
                    labels: { style: { colors: '#ffffff' } },
                    axisBorder: { show: false }, // removes x-axis line
                    axisTicks: { show: false }   // removes small ticks under labels
                },

                yaxis: {
                    title: { text: undefined },  // no label
                    labels: { show: false }      // no numbers
                },
                grid: {
                    yaxis: { lines: { show: false } } // no horizontal lines
                },
                legend: { position: 'bottom', labels: { colors: '#ffffff' } },
                tooltip: {
                    custom: function ({ series, seriesIndex, dataPointIndex }: any) {
                        const region = sortedRegions?.[dataPointIndex];
                        if (!region) {
                            return `<div style="background:#000;color:#fff;padding:6px;border-radius:4px;">No data</div>`;
                        }

                        const stats = regionStats[region] || { online: 0, offline: 0 };
                        const { online, offline } = stats;

                        return `
                <div style="background:#000;color:#fff;border-radius:4px;overflow:hidden;font-size:12px;min-width:140px;">
                    <div style="background:#111;padding:4px 8px;font-weight:bold;">
                        ${formatRegionName(region)}
                    </div>
                    <div style="background:#15FF00;color:#000;padding:4px 8px;">
                        Online: ${online} detector(s)
                    </div>
                    <div style="background:#a6a6a6;color:#fff;padding:4px 8px;">
                        Offline: ${offline} detector(s)
                    </div>
                </div>
            `;
                    }
                },
                fill: { opacity: 1 }
            }));

        }


        else {
            // Single region → donut
            const region = selectedRegions[0];
            const detectors = detectorDataRegionalized[region] || [];
            const onlineCount = detectors.filter(d => d.online).length;
            const offlineCount = detectors.filter(d => !d.online).length;

            setIsStackedBar(false);
            setChartSeries([onlineCount, offlineCount]);
            setChartOptions((prev: any) => ({
                ...prev,
                labels: ['Online', 'Offline'],
                colors: ['#15FF00', '#a6a6a6'],
                legend: { position: 'bottom', labels: { colors: '#ffffff' } },
                tooltip: { y: { formatter: (val: number) => `${val} detector(s)` } },
                plotOptions: { pie: { donut: { size: '50%' }, expandOnClick: false } },
                dataLabels: { enabled: true, style: { colors: ['#ffffff'] }, formatter: (val: number) => `${val.toFixed(1)}%` }
            }));
        }
    }, [selectedRegions, detectorDataRegionalized, detectorDataMonitoring]);


    // ----------------------------------------------- Chart functions -----------------------------------------------


    return (
        <div className='main-hub-dash-and-map'>

            <div className="main-hub-map">
                <MapContainer
                    center={[0.00, 0.00]}
                    zoom={2}
                    style={{ width: "100%", height: "100%" }}
                    ref={mapRef}>

                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    {/* <SearchControl /> */}

                    {filteredDetectors.map((detector) => {
                        const lat = Number(detector.status.gps_lat);
                        const lng = Number(detector.status.gps_long);

                        if (!lat || !lng) return null;

                        return (
                            <Marker
                                key={detector.mac}
                                position={[lat, lng]}
                                icon={getColorForMarker(detector.online, Number(detector.status.memory))}
                                ref={el => (markerRefs.current[detector.mac] = el)}
                            >
                                <Popup>
                                    <img src={fnnPopupLogo} alt="" className="fnn-popup-logo-backend" />
                                    <div className='new-popup-content'>
                                        <p>
                                            <strong>Detector:</strong>{" "}
                                            <span style={{ color: '#28aee1' }}>
                                                {detector.status.site_name || detector.mac}
                                            </span>
                                        </p>
                                        <p><strong>MAC:</strong> {detector.mac}</p>
                                        <p>
                                            <strong>Status:</strong>{" "}
                                            <span style={{ color: detector.online ? 'green' : 'red' }}>
                                                {detector.online ? 'Online' : 'Offline'}
                                            </span>
                                        </p>
                                        <p><strong>Latitude:</strong> {lat}</p>
                                        <p><strong>Longitude:</strong> {lng}</p>
                                        <div className='pop-changeableValues'>
                                            <strong> Memory: </strong>
                                            <span style={{ color: getColorForMemory(Number(detector.status.memory)) }}>
                                                {detector.status.memory}
                                            </span>
                                        </div>
                                        <div className='pop-changeableValues'>
                                            <strong> GPS Fix: </strong>
                                            <span style={{ color: getColorForGPSFix(detector.status.gps_fix) }}>
                                                {detector.status.gps_fix ? "Yes" : "No"}
                                            </span>
                                        </div>
                                        <div className='google-maps-button-div'
                                            onClick={() =>
                                                window.open(
                                                    `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                                                    "_blank",
                                                    "noopener,noreferrer"
                                                )
                                            }
                                        >
                                            <img src={googleMapsLogo} alt='gmaps' />
                                            <p>Google Maps</p>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>

            <div className='main-hub-dash'>
                <h2>Detector Information</h2>

                <div style={{ width: '100%' }}>
                    <Select
                        className='mySelect'
                        isMulti
                        options={regionOptions}
                        value={regionOptions.filter(opt =>
                            selectedRegions.includes(opt.value)
                        )} // ✅ this is okay once regionOptions is populated
                        onChange={handleRegionChange}
                        styles={customStyles}
                        // closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        placeholder="Select region(s)..."
                        components={{ ValueContainer }}
                        isLoading={regions.length === 0} // optional, disables until loaded
                    />

                </div>

                <div className='main-hub-sum-and-chart'>

                    <div className="main-hub-chart-wrapper">
                        <h2>Detector Status</h2>
                        <div
                            className="main-hub-chart"
                            style={{
                                minWidth:
                                    selectedRegions.includes("All") || selectedRegions.includes("All + Test Detectors")
                                        ? `${Object.keys(detectorDataRegionalized).length * 100}px`
                                        : selectedRegions.length > 4
                                            ? `${selectedRegions.length * 100}px`
                                            : '100%',
                            }}
                        >
                            <Chart
                                options={chartOptions}
                                series={chartSeries}
                                type={isStackedBar ? "bar" : "donut"}
                                width="100%"
                                // height={isStackedBar ? 300 : 300}
                                height="100%"
                            />
                        </div>
                    </div>




                    <div className="main-hub-region-cards">

                        <div className="main-hub-det-summary">
                            <div
                                className={`det-summary-item ${statusFilter === "all" ? "active" : ""}`}
                                onClick={() => setStatusFilter("all")}
                            >
                                <h2>Total</h2>
                                <h1>{selectedDetectors.length}</h1>
                            </div>

                            <div
                                className={`det-summary-item ${statusFilter === "online" ? "active" : ""}`}
                                onClick={() => setStatusFilter("online")}
                            >
                                <h2>Online</h2>
                                <h1>{selectedDetectors.filter(det => det.online).length}</h1>
                            </div>

                            <div
                                className={`det-summary-item ${statusFilter === "offline" ? "active" : ""}`}
                                onClick={() => setStatusFilter("offline")}
                            >
                                <h2>Offline</h2>
                                <h1>{selectedDetectors.filter(det => !det.online).length}</h1>
                            </div>
                        </div>

                        <div className="main-hb-region-cards">

                            {(selectedRegions.includes("All")
                                ? Object.keys(detectorDataRegionalized)
                                : selectedRegions.filter(r => r !== "All + Test Detectors")
                            ).map(region => {
                                const detectors = detectorDataRegionalized[region] || [];
                                const onlineCount = detectors.filter(d => d.online).length;
                                const offlineCount = detectors.filter(d => !d.online).length;

                                const expanded = expandedRegions[region] ?? false;

                                return (
                                    <div key={region} className="main-hub-region-card">
                                        {/* Region title + status summary */}
                                        <div
                                            className="main-hub-region-header"
                                            onClick={() =>
                                                setExpandedRegions(prev => ({
                                                    ...prev,
                                                    [region]: !expanded
                                                }))
                                            }
                                        >
                                            <h1>{formatRegionName(region)}</h1>

                                            <div className="main-hub-status-display-wrapper">
                                                <div className="main-hub-status-display ondet">{onlineCount}</div>
                                                |
                                                <div className="main-hub-status-display offdet">{offlineCount}</div>
                                                {expanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </div>
                                        </div>

                                        {/* Detector buttons - only show when expanded */}
                                        {expanded && (
                                            <div className="main-hub-dets">
                                                {detectors
                                                    .filter(detector => {
                                                        if (statusFilter === "online") return detector.online;
                                                        if (statusFilter === "offline") return !detector.online;
                                                        return true; // "all"
                                                    })
                                                    .map(detector => (
                                                        <button
                                                            key={detector.mac}
                                                            onClick={() => openPopup(detector.mac)}
                                                            style={{
                                                                color: detector.online ? "#15FF00" : "#a6a6a6"
                                                            }}
                                                        >
                                                            {detector.status.site_name || detector.mac}
                                                        </button>
                                                    ))}
                                            </div>
                                        )}

                                    </div>
                                );
                            })}

                        </div>
                    </div>



                </div>

            </div>

        </div>
    );
};


export default MainHub